import { z } from 'zod';
import { DDT_CARICO_CAUSALI, type DdtCaricoCausale } from './ddt-causali';
import { posDdtPassivoOrderAllocationInputSchema } from './procurement-receipts';
import { posDocumentScannerStateSchema } from './document-scanner';
import { posDdtDocumentSerialSchema, resolvePosDdtDocumentSerials } from './ddt-identifiers';

const ddtCaricoCausaleSchema = z.enum(
  DDT_CARICO_CAUSALI as [DdtCaricoCausale, ...DdtCaricoCausale[]],
);

const ddtStatusSchema = z.enum([
  'bozza',
  'importato',
  'confermato',
  'ricevuto',
  'fatturato',
  'caricato',
  'caricato_parziale',
  'escluso',
  'annullato',
]);

const ddtNumber = z.coerce.number().finite();
const optionalNullableString = (max: number) => z.string().max(max).nullable().optional();

/**
 * Canonical DDT passivo line write contract.
 *
 * Documentary serial/supplier snapshots are persisted separately from
 * physical receipt verification. `unit_cost` is intentionally not present.
 */
export const posDdtPassivoItemInputSchema = z.object({
  id: z.string().uuid().optional(),
  /**
   * Documentary N:N provenance.  A DDT row never carries a scalar PO-line
   * foreign key: one delivery row may fulfil more than one PO row.
   */
  order_allocations: z.array(
    posDdtPassivoOrderAllocationInputSchema.omit({ ddt_passivo_item_id: true }),
  ).max(1000).optional(),
  product_id: z.string().nullable().optional(),
  product_name: optionalNullableString(255),
  name: optionalNullableString(255),
  description: optionalNullableString(2000),
  sku: optionalNullableString(100),
  ean: optionalNullableString(32),
  quantity: ddtNumber.nonnegative().optional(),
  quantity_ordered: ddtNumber.nonnegative().nullable().optional(),
  quantity_shipped: ddtNumber.nonnegative().nullable().optional(),
  unit_of_measure: z.string().max(20).optional(),
  /** Gross/document price before discount; null is different from zero. */
  unit_price: ddtNumber.nonnegative().nullable().optional(),
  vat_rate: ddtNumber.min(0).max(100).nullable().optional(),
  vat_code: optionalNullableString(64),
  vat_nature: optionalNullableString(10),
  riferimento_normativo: optionalNullableString(255),
  discount_percent: ddtNumber.min(0).max(100).nullable().optional(),
  line_total: ddtNumber.nonnegative().nullable().optional(),
  lot_number: optionalNullableString(100),
  notes: optionalNullableString(20000),
  variant_id: z.string().uuid().nullable().optional(),
  expiry_date: z.string().date().nullable().optional(),
  expiration_date: z.string().date().nullable().optional(),
  production_date: z.string().date().nullable().optional(),
  document_serials: z.array(posDdtDocumentSerialSchema).max(3000).optional(),
  serial_numbers: z.array(z.string().min(1).max(255)).max(3000).optional(),
  serials: z.array(z.object({
    kind: z.string().min(1).max(64).optional(),
    serial_number: z.string().min(1).max(255),
  }).strict()).max(3000).optional(),
  supplier_sku: z.string().max(255).nullable().optional(),
  supplier_product_code: z.string().max(255).nullable().optional(),
}).strict();
export type PosDdtPassivoItemInput = z.infer<typeof posDdtPassivoItemInputSchema>;

/**
 * Header fields shared by POST and PUT.  The supplier/OCR enrichment fields
 * are accepted because the import UIs send them; persistence remains owned by
 * the existing DDT/import data model.
 */
const posDdtPassivoDocumentFields = {
  ddt_number: optionalNullableString(255),
  ddt_date: optionalNullableString(64),
  supplier_id: optionalNullableString(255),
  supplier_name: optionalNullableString(255),
  supplier_vat: optionalNullableString(64),
  supplier_address: optionalNullableString(2000),
  supplier_city: optionalNullableString(255),
  supplier_zip: optionalNullableString(32),
  supplier_province: optionalNullableString(32),
  supplier_country: optionalNullableString(128),
  supplier_sdi: optionalNullableString(64),
  supplier_pec: optionalNullableString(255),
  supplier_email: optionalNullableString(255),
  supplier_phone: optionalNullableString(64),
  recipient_name: optionalNullableString(255),
  recipient_address: optionalNullableString(2000),
  warehouse_id: optionalNullableString(255),
  company_id: z.string().uuid().nullable().optional(),
  scanner_state: posDocumentScannerStateSchema.optional(),
  carrier_name: optionalNullableString(255),
  carrier_plate: optionalNullableString(64),
  transport_mode: optionalNullableString(128),
  appearance: optionalNullableString(255),
  packages: ddtNumber.nonnegative().nullable().optional(),
  weight_kg: ddtNumber.nonnegative().nullable().optional(),
  notes: optionalNullableString(20000),
  import_source: z.enum(['ai', 'api', 'manual']).nullable().optional(),
  import_discrepancies: z.unknown().nullable().optional(),
  order_ref: optionalNullableString(255),
  total_amount: ddtNumber.nonnegative().nullable().optional(),
  total_vat: ddtNumber.nonnegative().nullable().optional(),
  currency: optionalNullableString(16),
  causale: ddtCaricoCausaleSchema.optional(),
};

const posDdtPassivoCreateObjectSchema = z.object({
  ...posDdtPassivoDocumentFields,
  purchase_order_ids: z.array(z.string().uuid()).max(100).optional(),
  order_item_metadata: z.array(z.object({
    purchase_order_item_id: z.string().uuid(),
    ean: optionalNullableString(32),
    document_serials: z.array(posDdtDocumentSerialSchema).max(3000).optional(),
    lot_number: optionalNullableString(100),
    notes: optionalNullableString(20000),
    expiration_date: z.string().date().nullable().optional(),
    production_date: z.string().date().nullable().optional(),
  }).strict()).max(3000).optional(),
  status: ddtStatusSchema.optional(),
  items: z.array(posDdtPassivoItemInputSchema).optional(),
}).strict();

function refineInitialDdtStatus(
  source: 'ai' | 'api' | 'manual' | null | undefined,
  status: z.infer<typeof ddtStatusSchema> | null | undefined,
  ctx: z.RefinementCtx,
): void {
  const effectiveStatus = status ?? 'bozza';
  const preparatory = source === 'ai' || source === 'api'
    ? ['bozza', 'importato']
    : ['bozza'];
  if (!preparatory.includes(effectiveStatus)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['status'],
      message: source === 'ai' || source === 'api'
        ? 'Gli import AI/API possono nascere soltanto in bozza o importato.'
        : 'I DDT manuali o senza fonte devono nascere in bozza.',
    });
  }
}

/** Strict POST /pos/ddt-passivi payload, limited to documentary preparation. */
export const posDdtPassivoCreateSchema = posDdtPassivoCreateObjectSchema.superRefine(
  (document, ctx) => refineInitialDdtStatus(document.import_source, document.status, ctx),
);
export type PosDdtPassivoCreateInput = z.infer<typeof posDdtPassivoCreateSchema>;

/**
 * Strict PUT line payload.
 *
 * `items`, when present, is the complete desired DDT line collection. Existing
 * rows carry their stable id; a row without id is a new canonical DDT item.
 * This makes header-only manual drafts editable without giving a client any
 * authority over server-generated row identities.
 */
export const posDdtPassivoUpdateItemSchema = posDdtPassivoItemInputSchema.partial().extend({
  id: z.string().uuid().optional(),
}).strict();
export type PosDdtPassivoUpdateItem = z.infer<typeof posDdtPassivoUpdateItemSchema>;

export const posDdtPassivoUpdateSchema = z.object({
  company_id: z.string().uuid().nullable().optional(),
  scanner_state: posDocumentScannerStateSchema.optional(),
  ddt_date: optionalNullableString(64),
  supplier_id: optionalNullableString(255),
  supplier_name: optionalNullableString(255),
  supplier_vat: optionalNullableString(64),
  warehouse_id: optionalNullableString(255),
  carrier_name: optionalNullableString(255),
  carrier_plate: optionalNullableString(64),
  transport_mode: optionalNullableString(128),
  appearance: optionalNullableString(255),
  packages: ddtNumber.nonnegative().nullable().optional(),
  weight_kg: ddtNumber.nonnegative().nullable().optional(),
  notes: optionalNullableString(20000),
  causale: ddtCaricoCausaleSchema.optional(),
  /** Complete replacement of the line collection; omit to patch header only. */
  items: z.array(posDdtPassivoUpdateItemSchema).max(1000).optional(),
}).strict();
export type PosDdtPassivoUpdateInput = z.infer<typeof posDdtPassivoUpdateSchema>;

/**
 * Import is still a DDT write, but has a required source discriminator.  It
 * intentionally reuses the canonical document/line shapes instead of a
 * second permissive import-only line contract.
 */
// Extend the underlying object before applying source-aware refinement:
// ZodEffects/pipe schemas intentionally do not expose `.extend()`.
export const posDdtPassivoImportSchema = posDdtPassivoCreateObjectSchema.extend({
  source: z.enum(['ai', 'api', 'manual']),
  status: ddtStatusSchema.nullable().optional(),
}).strict().superRefine((document, ctx) => {
  if (document.import_source != null && document.import_source !== document.source) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['import_source'],
      message: 'La fonte import duplicata non coincide con source.',
    });
  }
  refineInitialDdtStatus(document.source, document.status, ctx);
});
export type PosDdtPassivoImportInput = z.infer<typeof posDdtPassivoImportSchema>;

const wireDate = z.union([z.string(), z.date()]);

/** Typed detail DTO returned by GET /pos/ddt-passivi/:id. */
export const posDdtPassivoItemRowSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  ddt_passivo_id: z.string(),
  order_allocations: z.array(z.object({
    id: z.string(),
    purchase_order_item_id: z.string(),
    quantity_declared: z.union([z.string(), z.number()]),
    /** Canonical physical quota attributed to this exact DDT↔PO pair. */
    quantity_received: z.union([z.string(), z.number()]),
    quantity_remaining: z.union([z.string(), z.number()]),
    /** Human-readable number of the linked purchase order (operator-facing label). */
    order_number: z.string().nullable().optional(),
    created_at: wireDate,
  }).passthrough()).default([]),
  product_id: z.string().nullable(),
  product_name: z.string(),
  sku: z.string().nullable(),
  /**
   * Documentary supplier-code snapshot. It is never inferred from the
   * internal product SKU when absent.
   */
  supplier_sku: z.string().max(255).nullable(),
  /** Documentary barcode snapshot; null is never replaced with catalog data. */
  ean: z.string().max(32).nullable(),
  /** Documentary/scanned serial identifiers, distinct from physical receipt units. */
  serial_numbers: z.array(z.string().min(1).max(255)).max(3000),
  document_serials: z.array(posDdtDocumentSerialSchema).nullable().optional(),
  expiration_date: z.union([z.string(), z.date()]).nullable().optional(),
  production_date: z.union([z.string(), z.date()]).nullable().optional(),
  quantity: z.union([z.string(), z.number()]),
  /** All canonical physical allocation slots carrying this DDT line. */
  quantity_received: z.union([z.string(), z.number()]),
  quantity_remaining: z.union([z.string(), z.number()]),
  /** Received DDT quota with no PO side; it is deliberately not inferred. */
  quantity_unlinked_received: z.union([z.string(), z.number()]),
  /**
   * Server-derived edit authority for this documentary line. False after any
   * live or historical physical receipt or invoice fact; never accepted by a
   * DDT write payload.
   */
  mutable: z.boolean(),
  unit_of_measure: z.string().nullable(),
  unit_price: z.union([z.string(), z.number()]).nullable(),
  vat_rate: z.union([z.string(), z.number()]).nullable(),
  lot_number: z.string().nullable(),
  lot_id: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: wireDate,
  discount_percent: z.union([z.string(), z.number()]).nullable(),
  vat_code: z.string().nullable().optional(),
  vat_nature: z.string().nullable().optional(),
  riferimento_normativo: z.string().nullable().optional(),
  variant_id: z.string().nullable(),
  product_name_joined: z.string().nullable().optional(),
  product_sku: z.string().nullable().optional(),
}).passthrough().transform((row) => ({
  ...row,
  document_serials: resolvePosDdtDocumentSerials(row.document_serials, row.serial_numbers),
}));
export type PosDdtPassivoItemRow = z.infer<typeof posDdtPassivoItemRowSchema>;

export const posDdtPassivoDetailSchema = z.object({
  company_id: z.string().nullable().optional(),
  scanner_state: posDocumentScannerStateSchema.optional(),
  id: z.string(),
  tenant_id: z.string(),
  ddt_number: z.string(),
  ddt_date: wireDate.nullable(),
  supplier_id: z.string().nullable(),
  supplier_name: z.string().nullable(),
  /** Supplier name resolved from the registry (`pos_suppliers.name`) by the
   * detail query; the documentary snapshot `supplier_name` may be empty. */
  supplier_name_joined: z.string().nullable().optional(),
  supplier_vat: z.string().nullable(),
  warehouse_id: z.string().nullable(),
  warehouse_name: z.string().nullable().optional(),
  carrier_name: z.string().nullable(),
  carrier_plate: z.string().nullable(),
  transport_mode: z.string().nullable(),
  appearance: z.string().nullable(),
  packages: z.union([z.string(), z.number()]).nullable(),
  weight_kg: z.union([z.string(), z.number()]).nullable(),
  notes: z.string().nullable(),
  status: z.string(),
  attachments: z.unknown().optional(),
  /** Derived documentary graph; scalar header aliases are intentionally absent. */
  purchase_orders: z.array(z.object({
    id: z.string(),
    order_number: z.string().nullable(),
  }).passthrough()).default([]),
  supplier_invoices: z.array(z.object({
    id: z.string(),
    document_number: z.string().nullable(),
  }).passthrough()).default([]),
  import_source: z.string().nullable().optional(),
  import_discrepancies: z.unknown().nullable().optional(),
  causale: z.string().nullable().optional(),
  items: z.array(posDdtPassivoItemRowSchema),
}).passthrough();
export type PosDdtPassivoDetail = z.infer<typeof posDdtPassivoDetailSchema>;

// Explicit Input-suffixed aliases keep the shared contract discoverable for
// callers that use the naming convention of the other POS write schemas.
export const posDdtPassivoCreateInputSchema = posDdtPassivoCreateSchema;
export const posDdtPassivoUpdateInputSchema = posDdtPassivoUpdateSchema;
export const posDdtPassivoImportInputSchema = posDdtPassivoImportSchema;
